/**
 * Data provider — PostgREST, no Supabase.
 *
 * atomic-crm's own provider wraps `ra-supabase-core`, which is PostgREST
 * underneath, so the data path was never Supabase-specific. What WAS specific
 * is everything around it: auth, storage, and Edge Functions. Those are
 * replaced here.
 *
 * Where upstream calls an Edge Function, this calls SQL. That is not a
 * downgrade: `merge_contacts` already exists as a Postgres function in the
 * schema (upstream's own migration creates it), and the Edge Function was a
 * thin wrapper around it. Calling it through PostgREST's RPC endpoint runs the
 * same code with one less hop and one less thing to deploy.
 *
 * Auth-adjacent methods (signUp, updatePassword) are NOT reimplemented — they
 * throw. Keycloak owns identity here; a CRM-local password would be a second
 * credential for the same person, which is exactly the drift this platform
 * avoids elsewhere. Upstream reaches them only from its own signup and profile
 * screens, which are unreachable when the auth provider never offers them.
 */

import postgrestRestProvider, {
  defaultPrimaryKeys,
  defaultSchema,
} from "@raphiniert/ra-data-postgrest";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
} from "ra-core";

import { getCrmConfig } from "@/lib/crmConfig";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import { createHttpClient } from "./httpClient";
import { signAttachments, uploadAttachment } from "./attachments";

/**
 * Upstream reads list views rather than base tables for two resources, because
 * the views carry computed columns the list screens sort on. Same mapping
 * here — a base-table read would render but silently lose sorting.
 */
const LIST_VIEW: Record<string, string> = {
  companies: "companies_summary",
  contacts: "contacts_summary",
};


/**
 * The `q` filter, which is not a column.
 *
 * react-admin puts free-text search in `filter.q` — every autocomplete, the
 * global search, every list search box. PostgREST has no idea what `q` is, so
 * with `defaultListOp: "eq"` it went out as `q=eq.geurs holding` and came back
 *
 *     400  column companies_summary.q does not exist
 *
 * Upstream handles this in a `beforeGetList` lifecycle callback on its Supabase
 * provider; the overlay replaced the provider and left the callback behind, so
 * search has never worked — no autocomplete could find an existing record, and
 * the only way forward was the "create" option, which is how a contact ended up
 * being submitted with `company_id: "@@ra-create"`.
 *
 * The columns are upstream's, kept identical so a fork merge is a diff of one
 * list rather than a re-derivation. `email` and `phone` are jsonb on contacts
 * and cannot be matched with ilike; the schema carries `email_fts`/`phone_fts`
 * generated columns for exactly this, which is why they are mapped rather than
 * dropped.
 *
 * Wildcards are ours to add: PostgREST's `ilike` takes `*` and matches the
 * whole value without them, so `ilike.geurs` finds nothing while looking like
 * a working search.
 */
const SEARCH_COLUMNS: Record<string, string[]> = {
  contacts: [
    "first_name", "last_name", "company_name", "title",
    "email", "phone", "background",
  ],
  companies: ["name", "phone_number", "website", "zipcode", "city", "state_abbr"],
};

const FTS_COLUMN: Record<string, string> = {
  email: "email_fts",
  phone: "phone_fts",
};

/**
 * The activity log's two note columns, renamed for the components.
 *
 * The `activity_log` view emits `contact_note` and `deal_note`; the components
 * that render it read `activity.contactNote` and `activity.dealNote`. Upstream
 * renames them inside its Supabase data provider's `getList`, and the overlay
 * that replaced that provider carried the view mapping but not this one.
 *
 * The result was the dashboard crashing with "Something went wrong" the moment
 * an organization had a single note — `ActivityLogContactNoteCreated` reads
 * `contactNote.contact_id` off `undefined` — while every list page still
 * rendered, and an org with no notes yet (org 9, where the UI test runs) never
 * saw it. Same shape as the `q` filter above: what upstream did *around* its
 * provider is not carried by replacing the provider.
 */
const renameActivityNotes = (row: any) => ({
  ...row,
  contactNote: row.contact_note ?? undefined,
  dealNote: row.deal_note ?? undefined,
  contact_note: undefined,
  deal_note: undefined,
});

const applyTextSearch = (resource: string, params: GetListParams): GetListParams => {
  const q = params.filter?.q;
  if (!q) return params;
  const { q: _drop, ...filter } = params.filter;
  const columns = SEARCH_COLUMNS[resource];
  if (!columns) {
    // Better a search that ignores the term than a list that 400s: an
    // unsearchable resource still has to render. Loud, because the fix is to
    // add it above.
    console.warn(`[crm] no search columns for "${resource}"; ignoring q=${q}`);
    return { ...params, filter };
  }
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce(
        (acc, column) => ({
          ...acc,
          [`${FTS_COLUMN[column] ?? column}@ilike`]: `*${q}*`,
        }),
        {},
      ),
    },
  };
};

export const getDataProvider = (keycloakToken: () => Promise<string>) => {
  const httpClient = createHttpClient(keycloakToken);

  // apiUrl is resolved per call rather than captured: getCrmConfig() is a
  // promise, and postgrestRestProvider wants a string. Wrapping keeps the
  // provider construction synchronous, which is what CRM.tsx expects.
  let apiUrl = "/api/crm";
  void getCrmConfig().then((c) => {
    apiUrl = c.apiUrl;
  });

  const base = postgrestRestProvider({
    apiUrl,
    httpClient,
    defaultListOp: "eq",
    primaryKeys: defaultPrimaryKeys,
    schema: defaultSchema,
  });

  // Resources whose records carry attachments. Signing is per read rather
  // than per record so a page of notes costs ONE request, not one per file.
  const WITH_ATTACHMENTS = new Set(["contactNotes", "dealNotes", "sales"]);

  const withViews: DataProvider = {
    ...base,
    getList: async (resource: string, params: GetListParams) => {
      const res = await base.getList(
        LIST_VIEW[resource] ?? resource,
        applyTextSearch(resource, params),
      );
      if (resource === "activity_log") {
        return { ...res, data: res.data.map(renameActivityNotes) };
      }
      if (!WITH_ATTACHMENTS.has(resource)) return res;
      return { ...res, data: await signAttachments(res.data, keycloakToken) };
    },
    getOne: async (resource: string, params: any) => {
      const res = await base.getOne(resource, params);
      if (!WITH_ATTACHMENTS.has(resource)) return res;
      const [signed] = await signAttachments([res.data], keycloakToken);
      return { ...res, data: signed };
    },
    getManyReference: async (resource: string, params: any) => {
      // The path that actually renders a contact's notes: the contact page
      // asks for contactNotes BY contact_id, which is getManyReference, not
      // getList. Missing this hook is the difference between attachments
      // appearing everywhere and appearing nowhere anyone looks.
      const res = await base.getManyReference(
        resource, applyTextSearch(resource, params) as any);
      if (!WITH_ATTACHMENTS.has(resource)) return res;
      return { ...res, data: await signAttachments(res.data, keycloakToken) };
    },
  };

  const rpc = async (fn: string, body: unknown) => {
    const cfg = await getCrmConfig();
    const { json } = await httpClient(`${cfg.apiUrl}/rpc/${fn}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    return json;
  };

  const unsupported = (what: string) => () => {
    throw new Error(
      `${what} is not available: identity is managed in Keycloak, not the CRM`,
    );
  };

  const provider = {
    ...withViews,

    // ── identity is Keycloak's, not ours ────────────────────────────────
    signUp: unsupported("Sign-up"),
    updatePassword: unsupported("Changing a password here"),

    async salesCreate(body: any) {
      // A `sales` row is provisioned from platform users by the atomic driver,
      // so creating one here would produce a CRM user with no platform
      // identity behind it.
      throw new Error(
        "Team members are managed in the portal; they appear here automatically",
      );
    },

    async salesUpdate(id: Identifier, data: any, previousData?: any) {
      return withViews.update("sales", { id, data, previousData: previousData ?? { id } });
    },

    // ── business logic ──────────────────────────────────────────────────
    async unarchiveDeal(deal: any) {
      // Reopen at the top of its stage, and close the gap the deal left
      // behind, so the board does not end up with two deals at index 0.
      const { data: siblings } = await withViews.getList("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });
      await Promise.all(
        siblings.map((d: any, i: number) =>
          withViews.update("deals", {
            id: d.id,
            data: { index: i + 1 },
            previousData: d,
          }),
        ),
      );
      return withViews.update("deals", {
        id: deal.id,
        data: { archived_at: null, index: 0 },
        previousData: deal,
      });
    },

    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      // The Postgres function upstream's Edge Function wrapped.
      return rpc("merge_contacts", { loser_id: sourceId, winner_id: targetId });
    },

    async isInitialized() {
      const { json } = await httpClient(
        `${(await getCrmConfig()).apiUrl}/init_state?select=is_initialized`,
      );
      return Array.isArray(json) ? !!json[0]?.is_initialized : false;
    },

    async getConfiguration(): Promise<ConfigurationContextValue> {
      try {
        const { data } = await withViews.getOne("configuration", { id: 1 });
        return (data?.config as ConfigurationContextValue) ?? ({} as any);
      } catch {
        // A brand-new organization has no configuration row yet. Returning the
        // defaults beats failing the whole app on first load.
        return {} as ConfigurationContextValue;
      }
    },

    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await withViews.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  };

  // Attachments go to the portal, not a Supabase bucket. Same three resources
  // upstream hooks, so an attachment added anywhere in the UI is uploaded
  // before the row referencing it is written — the other order leaves a note
  // pointing at a file that does not exist yet.
  return withLifecycleCallbacks(provider as any, [
    {
      resource: "contactNotes",
      beforeSave: async (data: any) => uploadAttachments(data),
    },
    {
      resource: "dealNotes",
      beforeSave: async (data: any) => uploadAttachments(data),
    },
    {
      resource: "sales",
      beforeSave: async (data: any) => uploadAttachments(data),
    },
  ]) as typeof provider;
};

const uploadAttachments = async (data: any) => {
  if (!Array.isArray(data?.attachments)) return data;
  const attachments = await Promise.all(
    data.attachments.map((a: any) =>
      a?.rawFile instanceof File ? uploadAttachment(a) : a,
    ),
  );
  // Drop `src` on the way back in. It is a SIGNED, EXPIRING url added for
  // rendering; writing it back would persist a dead link into the record and,
  // worse, put a signature in the database where the next reader would trust
  // it after it stopped meaning anything.
  return {
    ...data,
    attachments: attachments.map(({ src, ...rest }: any) => rest),
  };
};

export type CrmDataProvider = ReturnType<typeof getDataProvider>;
